import { append, equals, negate, pipe, take, when } from 'ramda';
import {
  Children,
  type ReactElement,
  type ReactNode,
  cloneElement,
} from 'react';
import { Box, type BoxProps } from '@ironhack/design-system2/components/box';

import { WRAPPER_PADDING } from '@/app/components/client/module-wrapper/wrapper-padding';

const sizes = ['sm', 'md', 'lg'] as const;

type Props = {
  below?: (typeof sizes)[number];
  children?: ReactNode;
  withPadding?: boolean;
  withScrollbar?: boolean;
} & BoxProps;

export const CancelWrap = (props: Props): ReactElement => {
  const {
    below,
    children,
    withPadding = false,
    withScrollbar = false,
    ...boxProps
  } = props;

  const styles = below
    ? {
        childWidth: { base: 'min-content', [below]: 'auto' },
        overflow: { base: 'auto', [below]: 'hidden' } as const,
        // make a padding array ending in 0 of the correct length for the size
        // (0 means that there is no canceling effect)
        padding: pipe<[typeof WRAPPER_PADDING], number[], number[]>(
          take(sizes.findIndex(equals(below)) + 1),
          append(0)
        )(WRAPPER_PADDING),
      }
    : {
        childWidth: 'min-content',
        overflow: 'auto' as const,
        padding: WRAPPER_PADDING,
      };
  const margin = styles.padding.map(
    when(Boolean, negate) as (p: number | null) => number
  );

  return (
    <Box
      mx={margin}
      overflowX={styles.overflow}
      {...(withPadding && { px: styles.padding })}
      {...(!withScrollbar && {
        sx: {
          scrollbarWidth: 'none',
          '::-webkit-scrollbar': { display: 'none' },
        },
      })}
      {...boxProps}
    >
      {Children.map(children, (child) =>
        // @ts-expect-error -- Couldn't find a good explation for the error here. Feel free to try to fix it.
        cloneElement(child, { w: styles.childWidth })
      )}
    </Box>
  );
};
